import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Grid,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Stack,
  Flex,
  Icon,
} from '@chakra-ui/react'
import {
  AddressForm,
  SelectAddress,
  SelectAddressItem,
  Summary,
  TableOrder,
} from '@datawowio/payment-ui'
import { capitalize } from 'lodash'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { IoCardOutline, IoCashOutline } from 'react-icons/io5'

import { withCheckoutPage, useCheckoutStore } from '@/features/payments/stores'
import { useCheckoutServices } from '@/features/payments/services/CheckoutServices'
import {
  CheckoutCompanyForm,
  CheckoutCompanyFormValues,
} from '@/features/free-trial/components/CheckoutCompanyForm'
import { CheckoutSummary } from '@/features/payments/components/CheckoutSummary'
import { WhtOption, WhtPercent } from '@/features/payments/types'
import { CustomAddressItem } from '@/features/payments/components/CustomAddressItem'
import { ManualPaymentInstructions } from '../../../free-trial/components/ManualPaymentInstructions'
import { logEcommerce } from '@/lib/stats'
import { t } from '@/lib/helpers'
import { ERROR_TOAST_CONFIG } from '@/lib/constants'

const mapAddressToFormValues = (
  addr: any,
): Partial<CheckoutCompanyFormValues> => {
  if (!addr) return {}
  return {
    name: addr.name,
    tax_id: addr.taxId || addr.tax_id || addr.taxpayer_id,
    address: addr.address || addr.details,
    email: addr.email,
    phone: addr.phone || addr.phone_number,
    tax_id_type: addr.tax_id_type || addr.taxIdType,
    branch_type: addr.branch_type,
    branch_name: addr.branch_name,
    sub_district: addr.sub_district,
    district: addr.district,
    province: addr.province,
    zip_code: addr.zip_code,
    primary: addr.primary,
  }
}

const EditCompanyAddressModal = ({
  isOpen,
  onClose,
  address,
}: {
  isOpen: boolean
  onClose: () => void
  address: any
}) => {
  const CheckoutServices = useCheckoutServices()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('views.features.packages.checkout.company_header')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {address && (
            <CheckoutCompanyForm
              defaultValues={mapAddressToFormValues(address)}
              onSubmit={(values) => {
                CheckoutServices.updateAddress(
                  {
                    id: address.id,
                    ...values,
                  },
                  {
                    onSuccess: onClose,
                  },
                )
              }}
              onCancel={onClose}
              isEditMode
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function CheckoutPage() {
  const toast = useToast()
  const {
    addressType,
    addresses,
    packageData,
    recurring,
    recurringType,
    selectedAddress,
    setAddressType,
    setRecurringType,
    setSelectedAddress,
  } = useCheckoutStore()
  const CheckoutServices = useCheckoutServices()

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setLoading] = useState(false)
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null)

  const [paymentMethod, setPaymentMethod] = useState('credit_card')
  const [whtPercent, setWhtPercent] = useState<WhtPercent>(0)

  const isWhtSelectable =
    addressType === 'company' && paymentMethod === 'bank_transfer'

  useEffect(() => {
    if (errors.selectedAddress) {
      toast({
        ...ERROR_TOAST_CONFIG,
        description: errors.selectedAddress as string,
      })
    }
  }, [errors])

  const selectedRecurring = recurring[recurringType]
  const whtOptions: WhtOption[] = selectedRecurring.wht_options || []
  const selectedWhtOption = isWhtSelectable
    ? whtOptions.find((o) => o.percent === whtPercent)
    : undefined
  const appliedWhtPercent: WhtPercent = selectedWhtOption
    ? selectedWhtOption.percent
    : 0

  const editingAddress = useMemo(() => {
    return addresses.find((addr) => addr.id === editingAddressId)
  }, [addresses, editingAddressId])

  const handleCloseEdit = () => {
    setEditingAddressId(null)
  }

  const { i18n } = useTranslation()
  const dueDate = dayjs().add(packageData.name === 'large' ? 7 : 14, 'day')
  const formattedDueDate = dueDate.locale(i18n.language).format('DD/MM/YYYY')

  const validateSummary = (values?: { selectedAddress?: number }) => {
    const errors: Record<string, string> = {}

    if (!(values ? values.selectedAddress : selectedAddress)) {
      errors.selectedAddress = t(
        'views.features.packages.checkout.errors.address_required',
      )
    }

    return errors
  }

  const handleSubmit = () => {
    setLoading(true)

    if (paymentMethod === 'bank_transfer') {
      const fullAddress = addresses.find((a) => a.id === selectedAddress)

      if (!fullAddress) {
        setLoading(false)
        return
      }

      CheckoutServices.createManualTransaction({
        payload: fullAddress,
        whtPercent: appliedWhtPercent,
        onError: () => setLoading(false),
      })
    } else {
      CheckoutServices.saveInformation({
        onSuccess: () =>
          logEcommerce('add_to_cart', {
            value: selectedRecurring.total_amount,
            items: [
              {
                item_id: `${packageData.name}_${recurringType}`,
                item_name: `${packageData.name}_${recurringType}`,
                price: selectedRecurring.total_amount,
                quantity: 1,
              },
            ],
          }),
        onError: () => setLoading(false),
      })
    }
  }

  return (
    <Box layerStyle="container">
      <Box layerStyle="mainContent" sx={{ padding: '48px 40px' }}>
        <Text sx={{ fontSize: '24px', mb: '45px', fontWeight: 500 }}>
          {t('views.features.packages.checkout.summary')}
        </Text>

        <Grid
          sx={{
            gap: '24px',
            gridTemplateColumns: '1fr 440px',
            alignItems: 'start',
          }}
        >
          <Grid sx={{ gridColumn: '1/2', gap: '24px' }}>
            <Box>
              <TableOrder
                id={packageData.id}
                name={capitalize(packageData.name)}
                recurring={recurringType}
                price={packageData.price}
                net={
                  selectedWhtOption
                    ? selectedWhtOption.total_amount
                    : packageData.net
                }
                onChangePackage={(data) => {
                  setRecurringType(data.recurring as any)
                }}
              />
            </Box>

            <Box sx={{ p: 6, border: '1px solid', borderColor: 'gray.200', borderRadius: '8px' }}>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                {t('Payment Method')}
              </Text>
              <RadioGroup
                onChange={(method) => {
                  setPaymentMethod(method)
                  setWhtPercent(0)
                }}
                value={paymentMethod}
              >
                <Stack direction="row" spacing={8}>
                  <Radio value="credit_card">
                    <Flex align="center">
                      <Icon as={IoCardOutline} mr={2} boxSize={5} />
                      <Text>{t('views.features.packages.checkout.withCreditDebit')}</Text>
                    </Flex>
                  </Radio>
                  <Radio value="bank_transfer">
                     <Flex align="center">
                      <Icon as={IoCashOutline} mr={2} boxSize={5} />
                      <Text>{t('views.features.packages.checkout.withBankTransfer')}</Text>
                    </Flex>
                  </Radio>
                </Stack>
              </RadioGroup>

              {paymentMethod === 'bank_transfer' && (
                <>
                  <Box m={4} p={3} bg="blue.50" borderRadius="md" fontSize="sm" color="blue.800">
                    <Text>
                      {t('views.features.packages.checkout.note')}
                    </Text>
                  </Box>

                  <ManualPaymentInstructions
                    finalPrice={
                      selectedWhtOption
                        ? selectedWhtOption.total_amount
                        : selectedRecurring.amount
                    }
                    dueDate={formattedDueDate}
                  />
                </>
              )}
            </Box>

            <Box>
              <SelectAddress
                addressType={addressType}
                onChangeType={(type) => {
                  setAddressType(type)
                  setWhtPercent(0)
                }}
                renderAddressForm={({ onClose }) => {
                  if (addressType === 'company') {
                    return (
                      <Box maxH="70vh" overflowY="auto" px={1}>
                        <CheckoutCompanyForm
                          onSubmit={(values) => {
                            CheckoutServices.createAddress(values, {
                              onSuccess: onClose,
                            })
                          }}
                          onCancel={onClose}
                        />
                      </Box>
                    )
                  }
                  return (
                    <AddressForm
                      addressType={addressType}
                      onSubmit={(values) => {
                        CheckoutServices.createAddress(values, {
                          onSuccess: onClose,
                        })
                      }}
                    />
                  )
                }}
              >
                {addresses
                  .filter((addr) => addr.taxPayerType === addressType)
                  .map((addr) => {
                    const {
                      id: addressId,
                      name,
                      address,
                      primary,
                      taxId,
                      taxPayerType,
                    } = addr

                    if (addressType === 'company') {
                      return (
                        <CustomAddressItem
                          key={addressId}
                          id={addressId}
                          name={name}
                          address={address}
                          taxId={taxId}
                          primary={primary}
                          isSelected={selectedAddress === addressId}
                          onDelete={() => {
                            CheckoutServices.deleteAddress(addressId)
                          }}
                          onEdit={() => {
                            setEditingAddressId(addressId)
                          }}
                          onClick={() => {
                            setSelectedAddress(addressId)
                          }}
                          addressType={taxPayerType}
                        />
                      )
                    }

                    return (
                      <SelectAddressItem
                        key={addressId}
                        id={addressId}
                        name={name}
                        address={address}
                        taxId={taxId}
                        primary={primary}
                        isSelected={selectedAddress === addressId}
                        onDelete={() => {
                          CheckoutServices.deleteAddress(addressId)
                        }}
                        onEdited={(values) => {
                          CheckoutServices.updateAddress({
                            id: addressId,
                            ...values,
                          })
                        }}
                        onClick={() => {
                          setSelectedAddress(addressId)
                        }}
                        addressType={taxPayerType}
                      />
                    )
                  })}
              </SelectAddress>
            </Box>
          </Grid>

          <Box sx={{ gridColumn: '2/3' }}>
            {isWhtSelectable ? (
              <CheckoutSummary
                name={capitalize(packageData.name)}
                net={selectedRecurring.amount}
                total={Math.max(selectedRecurring.total_amount, 0)}
                vat={selectedRecurring.vat}
                vatPercent={selectedRecurring.vat_percent}
                preVatTotal={selectedRecurring.before_vat}
                whtOptions={whtOptions}
                selectedWht={appliedWhtPercent}
                onChangeWht={setWhtPercent}
                onErrors={(err) => {
                  setErrors(err)
                }}
                validate={validateSummary}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                loadingText={t('views.features.packages.checkout.loading')}
                {...(selectedRecurring.prorated_discount && {
                  proRateDiscount: {
                    label: 'ส่วนลดมูลค่าคงเหลือแพ็กเกจ',
                    value: selectedRecurring.prorated_discount,
                  },
                })}
              />
            ) : (
              <Summary
                net={selectedRecurring.amount}
                total={Math.max(selectedRecurring.total_amount, 0)}
                vat={selectedRecurring.vat}
                vatPercent={selectedRecurring.vat_percent}
                preVatTotal={selectedRecurring.before_vat}
                name={capitalize(packageData.name)}
                onErrors={(err) => {
                  setErrors(err)
                }}
                extraValues={{
                  selectedAddress,
                }}
                validate={(values) => validateSummary(values)}
                displayAcceptTerms={false}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                loadingText={t('views.features.packages.checkout.loading')}
                showWHTException={addressType === 'company'}
                {...(selectedRecurring.prorated_discount && {
                  proRateDiscount: {
                    label: 'ส่วนลดมูลค่าคงเหลือแพ็กเกจ',
                    value: selectedRecurring.prorated_discount,
                  },
                })}
              />
            )}
          </Box>
        </Grid>
      </Box>

      <EditCompanyAddressModal
        isOpen={!!editingAddressId}
        onClose={handleCloseEdit}
        address={editingAddress}
      />
    </Box>
  )
}

export default withCheckoutPage(CheckoutPage)
